import React from "react";
import { Box, MenuItem, Select } from "@mui/material";
import PageHeader from "../../shared/components/PageHeader";
import SubjectSwitcherTitle from "../../shared/components/SubjectSwitcherTitle";
import ClassPill, { classPillStripSx } from "../../shared/components/ClassPill";

const termLabel = (term) =>
  term ? `${term.name}${term.is_semester_term ? " (semester)" : ""}` : "";

// Page-header definition for the legacy v1 class (Subject) layout. The title is
// the shared subjects dropdown (the same control the v2 SBG class layout uses);
// the tabs, term selector, and the multi-select class switch are this layout's
// own. They are deliberately different from the SBG header: the tabs point at v1
// pages, SBG has no term selector, and SBG switches one class at a time while this
// switch toggles several at once. v1 owns every list and the URL params, handing
// them across the bridge as plain data plus callbacks (the inert MemoryRouter is
// never used to navigate).
export default function SubjectHeader({
  subjectName,
  subjects = [],
  subjectId,
  onSelectSubject,
  tabs = [],
  activeKey,
  onNavigate,
  terms = [],
  selectedTermId,
  onTermChange,
  classes = [],
  classesActive,
  onToggleClass,
  onClearClasses,
}) {
  // Clamp the term to a value one of the rendered MenuItems carries; terms are
  // empty on first paint while v1 fetches them, and an unmatched value trips
  // MUI's out-of-range warning.
  const termValue = terms.some((t) => t.id === selectedTermId)
    ? selectedTermId
    : "";

  // The same merged standard + SBG rows the v2 Subjects index lists. This page
  // is always a standard-scope view, so the active entry is the standard row of
  // the current subject; SBG rows are marked with the small gray tag.
  const subjectItems = subjects.map((s) => {
    const isActive = !s.sbg && s.id === Number(subjectId);
    return {
      key: `${s.sbg ? "sbg" : "std"}-${s.id}`,
      label: s.name,
      tag: s.sbg_label ? "SBG" : null,
      selected: isActive,
      disabled: isActive,
      onSelect: () => onSelectSubject(s),
    };
  });

  return (
    <PageHeader
      stackActionsOnMobile
      title={
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            minWidth: 0,
            // On mobile the actions break to their own row, so the title has the
            // whole first row: let it fill so the term selector can reach the far
            // right edge. Desktop keeps the title content width beside the tabs.
            flexGrow: { xs: 1, md: 0 },
          }}
        >
          <SubjectSwitcherTitle
            subjectName={subjectName || "Loading..."}
            items={subjectItems}
          />
          <Select
            size="small"
            value={termValue}
            onChange={(e) => onTermChange(e.target.value)}
            displayEmpty
            inputProps={{ "aria-label": "Term" }}
            renderValue={(v) =>
              termLabel(terms.find((t) => t.id === v)) || "Select term"
            }
            sx={{
              minWidth: 140,
              flexShrink: 0,
              // Mobile only: push the term selector to the right edge of the title
              // row. Desktop keeps it tucked next to the subject switcher.
              ml: { xs: "auto", md: 0 },
            }}
          >
            {terms.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {termLabel(t)}
              </MenuItem>
            ))}
          </Select>
        </Box>
      }
      tabs={tabs.map((t) => ({
        key: t.key,
        label: t.label,
        onClick: () => onNavigate(t.url),
      }))}
      activeTab={activeKey}
      actions={
        // Always render the actions slot (even with no classes) so the header
        // keeps its title : tabs : actions structure and the tabs stay centered;
        // otherwise an empty actions slot collapses the layout and the tabs jump
        // left. The pills only appear when the subject actually has classes.
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}
        >
          <Box sx={classPillStripSx}>
            {classes.length > 0 && (
              <>
                <ClassPill
                  label="All"
                  active={false}
                  onClick={onClearClasses}
                />
                {classes.map((k) => (
                  <ClassPill
                    key={k.id}
                    label={k.abbreviation}
                    active={k.isActive || !classesActive}
                    onClick={() => onToggleClass(k.id)}
                  />
                ))}
              </>
            )}
          </Box>
        </Box>
      }
    />
  );
}
